import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint, faSearch, faFileExcel, faSyncAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import logoImage from '../assets/images/logo.jpg';
import signatureImage from '../assets/images/asign.png';
import QRCode from 'qrcode';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Fuse from 'fuse.js';

const BirthCertificateList = () => {
    const navigate = useNavigate();
    const [gdsrEntries, setGdsrEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);

    // Calculate new entries count
    const newEntriesCount = useMemo(() => {
        return gdsrEntries.filter(entry => entry.isNew).length;
    }, [gdsrEntries]);

    // Fuse.js for fuzzy search
    const fuse = useMemo(() => new Fuse(gdsrEntries, {
        keys: ['token_id', 'local_no', 'name', 'father_name', 'cnic', 'phone_no', 'status', 'dob'],
        threshold: 0.3,
        includeScore: true
    }), [gdsrEntries]);

    const fetchEntries = useCallback(async () => {
        try {
            console.log("Fetching birth certificate entries...");
            const response = await axios.post('https://chaman.gob.pk/eac_backend/birthcert.php', {
                action: 'get_records',
            });

            console.log("Birth cert response:", response.data);

            // Handle both response formats
            let dataArray = [];
            
            if (response.data && response.data.status === "success" && Array.isArray(response.data.data)) {
                // Format 1: {status: "success", data: [...]}
                dataArray = response.data.data;
            } else if (Array.isArray(response.data)) {
                // Format 2: Direct array [...]
                dataArray = response.data;
            } else {
                console.error('Unexpected response format:', response.data);
                setError('Unexpected data format received from server');
                setGdsrEntries([]);
                return;
            }

            if (dataArray && dataArray.length > 0) {
                const now = new Date();
                setLastRefreshTime(now);

                // Parse and attach timestamp, then sort by newest date+time
                const sortedEntries = dataArray
                    .map(entry => {
                        // Try to parse the date reliably
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp); // Newest first

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else {
                setGdsrEntries([]);
                console.log('No data received or empty array');
            }
        } catch (err) {
            console.error('Error fetching Birth Certificate entries:', err);
            setError('Failed to fetch Birth Certificate entries: ' + (err.message || 'Network error'));
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        // Initial load with small delay to show loading state
        const timer = setTimeout(() => {
            fetchEntries();
        }, 5);

        return () => clearTimeout(timer);
    }, [fetchEntries]);

    const refreshData = () => {
        setIsRefreshing(true);
        fetchEntries();
    };

    const filteredEntries = useMemo(() => {
        if (!searchTerm) return gdsrEntries;
        return fuse.search(searchTerm).map(result => result.item);
    }, [gdsrEntries, searchTerm, fuse]);

    const handleDelete = async (token_id) => {
        if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/birthcert.php', {
                    action: 'delete',
                    token_id,
                });
                if (response.data.status === 'success') {
                    setGdsrEntries(prev => prev.filter(entry => entry.token_id !== token_id));
                    setSelectedIds(prev => prev.filter(id => id !== token_id));
                } else {
                    throw new Error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                alert(`Error: ${error.message}`);
            }
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) {
            alert("No records selected for deletion.");
            return;
        }

        if (window.confirm(`Are you sure you want to delete ${selectedIds.length} selected records?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/birthcert.php', {
                    action: 'delete',
                    token_ids: selectedIds,
                });

                if (response.data.status === 'success') {
                    setGdsrEntries(prevEntries => prevEntries.filter(entry => !selectedIds.includes(entry.token_id)));
                    setSelectedIds([]);
                    alert("Selected records deleted successfully.");
                } else {
                    throw new Error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                alert(`Error: ${error.message}`);
            }
        }
    };

    const handleEdit = (entry) => {
        navigate('/dashboard/edit-birth-certificate', { state: { entry } });
    };

    const handleCheckboxChange = (token_id) => {
        setSelectedIds(prev =>
            prev.includes(token_id)
                ? prev.filter(id => id !== token_id)
                : [...prev, token_id]
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const [year, month, day] = dateString.split('-');
            if (day && month && year) {
                return `${day}-${month}-${year}`;
            }
            return dateString;
        } catch (e) {
            return dateString;
        }
    };

    const handlePrint = async () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.height;

        const selectedRecords = gdsrEntries.filter(entry => selectedIds.includes(entry.token_id));
        if (selectedRecords.length === 0) {
            alert('Please select at least one record to print.');
            return;
        }

        // Add logo image
        doc.setFontSize(10);
        const imageWidth = 0.8 * 28.5;
        const imageHeight = 0.6 * 28.8;
        const xPosition = 14;
        const yPosition = 16;
        doc.addImage(logoImage, 'PNG', xPosition, yPosition, imageWidth, imageHeight);

        const entry = selectedRecords[0];
        const formattedDate = formatDate(entry.date);

        const fixedTextWidth = 90;
        const rightAlignX = pageWidth - fixedTextWidth - -10;

        doc.setFontSize(11);
        doc.setFont('times', "normal");
        doc.text(`No DC (Birth-Certificate) /2023/ ${entry.local_no}`, rightAlignX, 18);

        const maxTextWidth = Math.max(
            doc.getTextWidth(`No DC (Birth-Certificate) /2023/ ${entry.local_no}`),
            doc.getTextWidth("OFFICE OF THE"),
            doc.getTextWidth("DEPUTY COMMISSIONER CHAMAN"),
            doc.getTextWidth(`Dated the ${formattedDate}`)
        );

        doc.text("OFFICE OF THE", rightAlignX + maxTextWidth / 2 - doc.getTextWidth("OFFICE OF THE") / 2, 23);
        doc.text("DEPUTY COMMISSIONER CHAMAN", rightAlignX + maxTextWidth / 2 - doc.getTextWidth("DEPUTY COMMISSIONER CHAMAN") / 2, 28);

        const datedText = `Dated the ${formattedDate}`;
        const datedTextWidth = doc.getTextWidth(datedText);
        doc.text(datedText, rightAlignX + maxTextWidth / 2 - datedTextWidth / 2, 33);

        doc.setFontSize(12);
        doc.setFont('times', "normal");
        doc.text('To,', 14, 47);
        doc.text('The Chief Officer,', 43.5, 55);
        doc.text('Municipal Corporation Chaman.', 43.5, 60);

        doc.setFontSize(13);
        doc.text('Subject:', 14, 70);
        doc.setFont('times', 'bold');
        doc.text('APPLICATION FORM FOR ISSUANCE OF BIRTH CERTIFICATE', 43.5, 70);
        doc.setFont('timeal', 'normal');
        doc.line(43.5, 71, 190, 71);

        doc.setFontSize(11);
        doc.setFont("normal");
        doc.text('Reference to your office letter No: 17/MCC dated 09th May, 2018 on the subject cited above.', 43.5, 78);
        doc.text('In pursuance of Principal Secretary to Chief Minister, Balochistan letter No. PS-CM/1-1/2014/', 43.5, 90);
        doc.text('1983/87 dated 10th April, 2014, the undersigned is pleased to accord approval for issuance of BIRTH certificate', 14, 95);
        doc.text('in favor of the following, and subject to completion of Codal formalities / necessary documents as prescribed /', 14, 100);
        doc.text('deposit of Government fee and also keep office record in safe hands.', 14, 105);

        const tableData = [
            ["Sr#", "Name", "S/D/W", "Guardian CNIC", "Date of Birth", "Remarks"],
            ...selectedRecords.map((entry, index) => [
                index + 1,
                entry.name || '',
                entry.father_name || '',
                entry.cnic || '',
                entry.dob ? formatDate(entry.dob) : "",
                entry.remarks || ""
            ]),
        ];

        const tableY = 120;
        doc.autoTable({
            head: [tableData[0]],
            body: tableData.slice(1),
            startY: tableY,
            theme: 'grid',
            headStyles: {
                fillColor: [0, 0, 0],
                textColor: [255, 255, 255],
                lineColor: [0, 0, 0],
                lineWidth: 0.5,
                fontSize: 10,
                fontStyle: 'bold',
                halign: 'center',
            },
            styles: {
                fontSize: 10,
                fontStyle: 'normal',
                textColor: [0, 0, 0],
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
            },
            columnStyles: {
                0: { cellWidth: 10, halign: 'center' },
                1: { cellWidth: 35, halign: 'left' },
                2: { cellWidth: 35, halign: 'left' },
                3: { cellWidth: 37, halign: 'center' },
                4: { cellWidth: 25, halign: 'center' },
                5: { cellWidth: 38, halign: 'center' },
            },
        });

        const footerY = doc.autoTable.previous.finalY + 30;
        const deputyTextX = 128;
        const deputyTextY = footerY;
        const signatureX = 155;
        const signatureY = deputyTextY - 25;

        doc.setFontSize(10).setFont('helvetica', 'normal');
        doc.text('ADDITIONAL DEPUTY COMMISSIONER', deputyTextX, deputyTextY);
        doc.text('CHAMAN', 155, deputyTextY + 5);

        let qrData = "Verified and issued Birth Certificates by the Office of the Deputy Commissioner, Chaman:\n";
        selectedRecords.forEach((entry, index) => {
            qrData += `${index + 1}. Name: ${entry.name || ''}\n`;
        });

        const qrCanvas = document.createElement('canvas');
        await QRCode.toCanvas(qrCanvas, qrData, { width: 100 });
        const qrDataURL = qrCanvas.toDataURL('image/png');

        doc.addImage(qrDataURL, 'PNG', 95, pageHeight - 40, 26, 26);

        window.open(doc.output('bloburl'));
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(gdsrEntries);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Birth Certificate Entries');
        XLSX.writeFile(workbook, 'Birth Certificate.xlsx');
    };

    const Row = ({ index, style }) => {
        const entry = filteredEntries[index];
        if (!entry) return null;

        return (
            <div style={style} className="table-row">
                <div className="table-cell">
                    <input
                        type="checkbox"
                        checked={selectedIds.includes(entry.token_id)}
                        onChange={() => handleCheckboxChange(entry.token_id)}
                    />
                </div>
                <div className="table-cell">{entry.token_id || 'N/A'}</div>
                <div className="table-cell">{formatDate(entry.date)}</div>
                <div className="table-cell">{entry.name || 'N/A'}</div>
                <div className="table-cell">{entry.father_name || 'N/A'}</div>
                <div className="table-cell actions btn-group">
                    <button onClick={() => handleEdit(entry)} title="Edit" className="edit-btn">
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button onClick={() => handleDelete(entry.token_id)} title="Delete" className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            </div>
        );
    };

    if (loading) return (
        <div className="loading-container">
            <div className="modern-loading">
                <div className="loading-wave">
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                </div>
                <p className="loading-text">Loading Birth Certificate Records...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="error-container">
            <div className="error-card">
                <h3>Error Loading Data</h3>
                <p>{error}</p>
                <div className="error-actions">
                    <button onClick={refreshData} className="retry-btn">
                        <FontAwesomeIcon icon={faSyncAlt} /> Retry
                    </button>
                    <button onClick={() => window.location.reload()} className="reload-btn">
                        Reload Page
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="certificate-dashboard">
            <div className="dashboard-header">
                <div className="header-left">
                    <h2>Birth Certificate Records</h2>
                    <div className="stats-container">
                        <span className="entries-count">Total: {gdsrEntries.length}</span>
                        {newEntriesCount > 0 && (
                            <span className="new-entries-count">New: {newEntriesCount}</span>
                        )}
                        {lastRefreshTime && (
                            <span className="refresh-time">
                                Last updated: {lastRefreshTime.toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                </div>

                <div className="header-right">
                    <div className="search-refresh-wrapper">
                        <button
                            onClick={refreshData}
                            className="refresh-button"
                            disabled={isRefreshing}
                            title="Refresh data"
                        >
                            <FontAwesomeIcon
                                icon={faSyncAlt}
                                spin={isRefreshing}
                                className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
                            />
                            {isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </button>

                        <div className="search-bar">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search by Token ID, Name, CNIC..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')}>
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="action-buttons">
                <button 
                    onClick={handlePrint} 
                    className="bprint-btn"
                    disabled={selectedIds.length === 0}
                    title="Print selected records"
                >
                    <FontAwesomeIcon icon={faPrint} /> Print Selected ({selectedIds.length})
                </button>
                
                {selectedIds.length > 0 && (
                    <button 
                        onClick={handleBulkDelete} 
                        className="delete-btn"
                        title="Delete selected records"
                    >
                        <FontAwesomeIcon icon={faTrash} /> Delete Selected ({selectedIds.length})
                    </button>
                )}
                
                <button 
                    onClick={exportToExcel} 
                    className="excel-btn"
                    title="Export to Excel"
                >
                    <FontAwesomeIcon icon={faFileExcel} /> Export to Excel
                </button>
            </div>

            <div className="table-container">
                <div className="table-header">
                    <div className="header-cell">Select</div>
                    <div className="header-cell">Token ID</div>
                    <div className="header-cell">Date</div>
                    <div className="header-cell">Name</div>
                    <div className="header-cell">Father Name</div>
                    <div className="header-cell">Actions</div>
                </div>

                <div className="table-body">
                    {filteredEntries.length > 0 ? (
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    height={height}
                                    itemCount={filteredEntries.length}
                                    itemSize={45}
                                    width={width}
                                >
                                    {Row}
                                </List>
                            )}
                        </AutoSizer>
                    ) : (
                        <div className="empty-state">
                            {searchTerm ? (
                                <>
                                    <FontAwesomeIcon icon={faSearch} size="3x" />
                                    <p>No records found matching "{searchTerm}"</p>
                                    <button onClick={() => setSearchTerm('')} className="clear-search-btn">
                                        Clear Search
                                    </button>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faInfoCircle} size="3x" />
                                    <p>No birth certificate records available</p>
                                    <p className="empty-subtext">Add new records using the Birth Certificate form</p>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="table-footer">
                    <div className="footer-info">
                        Showing {filteredEntries.length} of {gdsrEntries.length} entries
                        {searchTerm && ` (filtered)`}
                    </div>
                    <div className="footer-actions">
                        <div className="selection-info">
                            {selectedIds.length} record(s) selected
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BirthCertificateList;